import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, CircleSlash, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { fmtUnitTotals } from "@/lib/wmDigits";
import { wmMessage } from "@/lib/wmError";

const C = OPS.wm;

const STATE_STYLE = {
    recorded: { icon: CheckCircle2, cls: "text-[color:var(--success)]" },
    none: { icon: CircleSlash, cls: "text-[color:var(--ink-muted)]" },
    not_recorded: { icon: AlertCircle, cls: "text-[color:var(--danger)]" },
};

/**
 * «متابعة اليوم» — read-only, `wm_branch_manage` and above. Three states per
 * branch: سجّل / لا يوجد / لم يُسجّل, so the pilot can actually be measured.
 */
export function DayCoverageTable({ scope }) {
    const { t, lang } = useI18n();
    const [date, setDate] = useState(scope?.default_date || scope?.today || "");
    const [type, setType] = useState("waste");
    const [rows, setRows] = useState([]);
    const [units, setUnits] = useState({});
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        if (!date) return;
        setLoading(true);
        try {
            const res = await api.get("/waste-meals/day-coverage", { params: { date, type } });
            setRows(res.data.rows || []);
            setUnits(res.data.units || {});
        } catch (e) {
            toast.error(wmMessage(e, lang) || t("wm_offline_error"));
        } finally {
            setLoading(false);
        }
    }, [date, type, lang, t]);

    useEffect(() => { load(); }, [load]);

    // Unit KEYS never reach the screen: «١٣ حبة», not «١٣ piece».
    const unitNames = useMemo(() => {
        const m = {};
        for (const [k, v] of Object.entries(units)) m[k] = lang === "ar" ? v.name_ar : v.name_en;
        return m;
    }, [units, lang]);

    const counts = rows.reduce((a, r) => ({ ...a, [r.state]: (a[r.state] || 0) + 1 }), {});

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
                <Input data-testid={C.coverage.date} type="date" value={date} dir="ltr" lang="ar"
                       max={scope?.today} onChange={(e) => setDate(e.target.value)}
                       className="h-9 w-40" />
                <div className="flex gap-1 rounded-full border border-[color:var(--border-soft)] p-0.5">
                    {["waste", "meals"].map((tp) => (
                        <button key={tp} type="button" data-testid={`wm-coverage-type-${tp}`}
                                onClick={() => setType(tp)}
                                className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                                    type === tp
                                        ? "bg-[color:var(--brand-orange)] text-white"
                                        : "text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]"
                                }`}>
                            {tp === "waste" ? t("wm_tab_waste") : t("wm_tab_meals")}
                        </button>
                    ))}
                </div>
                {["recorded", "none", "not_recorded"].map((s) => (
                    <Badge key={s} variant="secondary" className="h-7 px-2.5 text-[11px]">
                        {t(`wm_coverage_state_${s}`)}: <bdi dir="ltr" className="ms-1">{counts[s] || 0}</bdi>
                    </Badge>
                ))}
            </div>

            {loading ? <InlineLoader /> : !rows.length ? (
                <p data-testid={C.coverage.empty}
                   className="rounded-[var(--radius-lg)] border border-dashed border-[color:var(--border-soft)] py-10 text-center text-sm text-[color:var(--ink-muted)]">
                    {t("wm_coverage_empty")}
                </p>
            ) : (
                <div className="overflow-hidden rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)]">
                    <table data-testid={C.coverage.table} className="w-full text-sm">
                        <thead className="bg-[color:var(--bg-surface-2)]">
                            <tr>
                                <th className="px-3 py-2 text-start text-xs font-bold text-[color:var(--ink-muted)]">{t("wm_branch")}</th>
                                <th className="px-3 py-2 text-start text-xs font-bold text-[color:var(--ink-muted)]">{t("wm_coverage_lines")}</th>
                                <th className="px-3 py-2 text-start text-xs font-bold text-[color:var(--ink-muted)]">{t("wm_grid_total")}</th>
                                <th className="px-3 py-2 text-start text-xs font-bold text-[color:var(--ink-muted)]">{t("wm_coverage_last_by")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const S = STATE_STYLE[r.state] || STATE_STYLE.not_recorded;
                                const Icon = S.icon;
                                return (
                                    <tr key={r.branch_id} data-testid={C.coverage.row(r.branch_id)}
                                        className="border-t border-[color:var(--separator)]">
                                        <td className="px-3 py-2.5">
                                            <span className="flex items-center gap-2">
                                                <Icon className={`h-4 w-4 shrink-0 ${S.cls}`} />
                                                <span className="truncate font-semibold text-[color:var(--ink)]">
                                                    {lang === "ar" ? r.name_ar : r.name_en || r.name_ar}
                                                </span>
                                                {r.is_active === false ? (
                                                    <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                                                        {t("wm_badge_inactive_branch")}
                                                    </Badge>
                                                ) : null}
                                            </span>
                                            <span data-testid={C.coverage.state(r.branch_id)}
                                                  className={`mt-0.5 block text-[11px] font-semibold ${S.cls}`}>
                                                {t(`wm_coverage_state_${r.state}`)}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2.5 text-xs tabular-nums text-[color:var(--ink-muted)]">
                                            <bdi dir="ltr">{r.lines}</bdi>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2.5 text-xs font-semibold text-[color:var(--ink)]">
                                            <bdi dir="ltr">{fmtUnitTotals(r.totals, unitNames, lang) || "—"}</bdi>
                                            {/* returns are recorded but not waste (D2): shown apart, never in the total */}
                                            {fmtUnitTotals(r.returns_totals, unitNames, lang) ? (
                                                <span data-testid={C.coverage.returns(r.branch_id)}
                                                      className="mt-0.5 block text-[10px] font-semibold text-[color:var(--brand-brown)]">
                                                    {t("wm_coverage_returns")}:{" "}
                                                    <bdi dir="ltr">{fmtUnitTotals(r.returns_totals, unitNames, lang)}</bdi>
                                                </span>
                                            ) : null}
                                        </td>
                                        <td className="px-3 py-2.5 text-xs text-[color:var(--ink-muted)]">
                                            {r.last_by || "—"}
                                            {r.last_at ? (
                                                <span className="block text-[10px] text-[color:var(--ink-faint)]">
                                                    <bdi dir="ltr">{(r.last_at || "").slice(11, 16)}</bdi>
                                                </span>
                                            ) : null}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
